import React, { useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import { storage, STORAGE_KEYS } from "../../utils/storage";
import { useIsMobile } from "../../App";

const SettingsScreen = () => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [importData, setImportData] = useState("");

  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { t } = useLocalization();
  const isMobile = useIsMobile();

  const exportData = () => {
    try {
      const data = {
        user: storage.getItem(STORAGE_KEYS.USER),
        chats: storage.getItem(STORAGE_KEYS.CHATS),
        settings: storage.getItem(STORAGE_KEYS.SETTINGS),
        theme: storage.getItem(STORAGE_KEYS.THEME),
        language: storage.getItem(STORAGE_KEYS.LANGUAGE),
        timestamp: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat-app-backup-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowExportModal(false);
    } catch (error) {
      alert(t("exportError"));
    }
  };

  const handleImportData = () => {
    try {
      const data = JSON.parse(importData);

      if (data.user) storage.setItem(STORAGE_KEYS.USER, data.user);
      if (data.chats) storage.setItem(STORAGE_KEYS.CHATS, data.chats);
      if (data.settings) storage.setItem(STORAGE_KEYS.SETTINGS, data.settings);
      if (data.theme) storage.setItem(STORAGE_KEYS.THEME, data.theme);
      if (data.language) storage.setItem(STORAGE_KEYS.LANGUAGE, data.language);

      alert(t("importSuccess"));
      setShowImportModal(false);
      setImportData("");
    } catch (error) {
      alert(t("importError"));
    }
  };

  const clearAllData = () => {
    storage.clear();
    alert(t("dataDeleted"));
    window.location.reload();
  };

  const containerStyles = {
    padding: "24px",
    maxWidth: "800px",
    margin: "0 auto",
    backgroundColor: theme.background,
    minHeight: "100%",
    height: "90vh",
    overflowY: "scroll",
    boxSizing: "border-box",
  };

  const sectionStyles = {
    backgroundColor: theme.surface,
    borderRadius: "0",
    border: `1px solid ${theme.border}`,
    marginBottom: "20px",
  };

  const sectionHeaderStyles = {
    padding: "16px 20px",
    backgroundColor: theme.isDark ? theme.border : theme.background,
    borderBottom: `1px solid ${theme.border}`,
    fontSize: "18px",
    fontWeight: "600",
    color: theme.text,
  };

  const settingItemStyles = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: `1px solid ${theme.border}`,
  };

  const settingLabelStyles = {
    fontSize: "16px",
    color: theme.text,
    flex: 1,
  };

  const settingDescStyles = {
    fontSize: "14px",
    color: theme.textSecondary,
    marginTop: "4px",
    maxWidth: isMobile ? "50vw" : "100%",
    whiteSpace: "normal",
  };

  const toggleSwitchStyles = (isOn) => ({
    position: "relative",
    width: "48px",
    height: "24px",
    borderRadius: "12px",
    backgroundColor: isOn ? theme.primary : theme.border,
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  });

  const toggleKnobStyles = (isOn) => ({
    position: "absolute",
    top: "2px",
    left: isOn ? "26px" : "2px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    transition: "left 0.3s ease",
  });

  const modalOverlayStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalStyles = {
    backgroundColor: theme.surface,
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "500px",
    width: "90%",
    border: `1px solid ${theme.border}`,
    maxHeight: "80vh",
    overflow: "auto",
  };

  const ToggleSwitch = ({ value, onChange }) => (
    <div style={toggleSwitchStyles(value)} onClick={() => onChange(!value)}>
      <div style={toggleKnobStyles(value)} />
    </div>
  );

  const LanguageSelector = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { currentLanguage, changeLanguage, getAvailableLanguages } =
      useLocalization();
    const { theme } = useTheme();
    const languages = getAvailableLanguages();
    const currentLang = languages.find((lang) => lang.code === currentLanguage);

    const dropdownStyles = {
      position: "relative",
      display: "inline-block",
    };

    const buttonStyles = {
      background: theme.background,
      border: `1px solid ${isHovered ? theme.primary : theme.border}`,
      borderRadius: "8px",
      padding: "10px 12px",
      color: theme.text,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "15px",
      transition: "all 0.2s",
      minWidth: "150px",
      justifyContent: "space-between",
      backgroundColor: isHovered
        ? theme.isDark
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.05)"
        : theme.background,
    };

    const dropdownContentStyles = {
      display: isOpen ? "block" : "none",
      position: "absolute",
      right: "0",
      top: "calc(100% + 4px)",
      backgroundColor: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      zIndex: 10,
      width: "100%",
      color: theme.text,
    };

    const languageItemStyles = {
      padding: "10px 12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      color: theme.text,
    };

    return (
      <div style={dropdownStyles}>
        <button
          style={buttonStyles}
          onClick={() => setIsOpen(!isOpen)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span>{currentLang?.nativeName}</span>
          <span style={{ fontSize: "12px" }}>▼</span>
        </button>

        <div style={dropdownContentStyles}>
          {languages.map((language) => (
            <div
              key={language.code}
              style={{
                ...languageItemStyles,
                backgroundColor:
                  currentLanguage === language.code
                    ? theme.primary
                    : "transparent",
                color: currentLanguage === language.code ? "white" : theme.text,
              }}
              onClick={async () => {
                try {
                  await changeLanguage(language.code);
                  setIsOpen(false);
                } catch (error) {
                  console.error("Error changing language:", error);
                  // Still close the dropdown even if there's an error
                  setIsOpen(false);
                }
              }}
            >
              <span>{language.nativeName}</span>
            </div>
          ))}
        </div>

        {isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9,
            }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>
    );
  };

  return (
    <div style={containerStyles}>
      {/* Основні налаштування */}
      <div style={sectionStyles}>
        <div style={sectionHeaderStyles}>{t("basicSettings")}</div>

        <div style={settingItemStyles}>
          <div>
            <div style={settingLabelStyles}>{t("darkTheme")}</div>
            <div style={settingDescStyles}>{t("darkThemeDesc")}</div>
          </div>
          <ToggleSwitch value={isDarkMode} onChange={toggleTheme} />
        </div>

        <div style={settingItemStyles}>
          <div>
            <div style={settingLabelStyles}>{t("language")}</div>
            <div style={settingDescStyles}>{t("languageDesc")}</div>
          </div>
          <LanguageSelector />
        </div>
      </div>

      {/* Резервне копіювання */}
      {/* <div style={sectionStyles}>
        <div style={sectionHeaderStyles}>{t("backup")}</div>

        <div style={settingItemStyles}>
          <div>
            <div style={settingLabelStyles}>{t("exportData")}</div>
            <div style={settingDescStyles}>{t("exportDataDesc")}</div>
          </div>
          <Button
            onClick={() => setShowExportModal(true)}
            variant="primary"
            size="small"
          >
            {t("export")}
          </Button>
        </div>

        <div style={{ ...settingItemStyles, borderBottom: "none" }}>
          <div>
            <div style={settingLabelStyles}>{t("importData")}</div>
            <div style={settingDescStyles}>{t("importDataDesc")}</div>
          </div>
          <Button
            onClick={() => setShowImportModal(true)}
            variant="secondary"
            size="small"
          >
            {t("import")}
          </Button>
        </div>
      </div> */}

      {/* Модальне вікно експорту */}
      {showExportModal && (
        <div
          style={modalOverlayStyles}
          onClick={() => setShowExportModal(false)}
        >
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: "16px", color: theme.text }}>
              {t("exportData")}
            </h3>
            <p style={{ marginBottom: "20px", color: theme.textSecondary }}>
              {t("exportDataModalDesc")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <Button
                onClick={() => setShowExportModal(false)}
                variant="secondary"
                size={isMobile ? "medium" : "small"}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                size={isMobile ? "medium" : "small"}
                onClick={exportData}
              >
                {t("download")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Модальне вікно імпорту */}
      {showImportModal && (
        <div
          style={modalOverlayStyles}
          onClick={() => setShowImportModal(false)}
        >
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: "16px", color: theme.text }}>
              {t("importData")}
            </h3>
            <p style={{ marginBottom: "16px", color: theme.textSecondary }}>
              {t("importDataModalDesc")}
            </p>
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              style={{
                width: "100%",
                height: "200px",
                padding: "12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "8px",
                backgroundColor: theme.background,
                color: theme.text,
                fontSize: "12px",
                fontFamily: "monospace",
                resize: "vertical",
                marginBottom: "20px",
              }}
              placeholder={t("pasteJsonData")}
            />
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => setShowImportModal(false)}
                variant="secondary"
              >
                {t("cancel")}
              </Button>
              <Button onClick={handleImportData} disabled={!importData.trim()}>
                {t("import")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Модальне вікно підтвердження видалення */}
      {showDeleteConfirmModal && (
        <div
          style={modalOverlayStyles}
          onClick={() => setShowDeleteConfirmModal(false)}
        >
          <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: "16px", color: theme.text }}>
              {t("deleteConfirmation")}
            </h3>
            <p style={{ marginBottom: "20px", color: theme.textSecondary }}>
              {t("deleteConfirmationDesc")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <Button
                onClick={() => setShowDeleteConfirmModal(false)}
                variant="secondary"
                size={isMobile ? "medium" : "small"}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={clearAllData}
                variant="danger"
                size={isMobile ? "medium" : "small"}
              >
                {t("deleteAll")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen;
